import type {
  GetMatchesCountData,
  GetNetworkMatchesData,
  SubmitRejectionFeedbackData,
  UpdateMatchStatusData,
  TrackIceBreakerUsageData,
  SubmitMatchFeedbackWithReasonsData,
} from '../types/match';
import { api, type ApiResponse } from '../utils';

const getOnboardingMatches = async (): Promise<ApiResponse> => {
  const { data: response } = await api.get('/dashboard/onboarding-matches');
  return response;
};

const updateMatchStatus = async (
  data: UpdateMatchStatusData
): Promise<ApiResponse> => {
  const response = await api.post(`/dashboard/decide-match`, data);
  return response.data;
};

const submitRejectionFeedback = async (
  data: SubmitRejectionFeedbackData
): Promise<ApiResponse> => {
  const { data: response } = await api.post(
    '/dashboard/submit-match-feedback',
    data
  );
  return response;
};

const getNetworkMatches = async (
  data: GetNetworkMatchesData
): Promise<ApiResponse> => {
  let endpoint = '/dashboard/list-matches';

  const { page, limit = 20, status, sub_status, start_date, end_date, event_id } = data;

  const params: Record<string, string> = {
    page: page.toString(),
    limit: limit.toString(),
    status,
  };

  // Add optional filters only if they have values
  if (sub_status) params.sub_status = sub_status;
  if (event_id) params.event_id = event_id;
  if (start_date) params.start_date = start_date;
  if (end_date) params.end_date = end_date;

  const queryString = new URLSearchParams(params).toString();

  endpoint += `?${queryString}`;

  const { data: response } = await api.get(endpoint);
  return response;
};

const getMatchCounts = async (
  data: GetMatchesCountData
): Promise<ApiResponse> => {
  let endpoint: string = '/dashboard/matches-count';
  const { start_date, end_date, event_id } = data;

  const params: Record<string, string> = {};
  if (start_date && end_date) {
    params.start_date = start_date;
    params.end_date = end_date;
  }
  // Multi-event: scope the counts to the selected event so the tab numbers
  // always agree with the filtered list.
  if (event_id) params.event_id = event_id;

  const queryString = new URLSearchParams(params).toString();
  if (queryString) endpoint += `?${queryString}`;

  const { data: response } = await api.get(endpoint);
  return response;
};

// === Phase 1.1: Match Explanation ===
const getMatchExplanation = async (
  matchId: string,
  forceRefresh = false
): Promise<ApiResponse> => {
  const params = forceRefresh ? '?force_refresh=true' : '';
  const { data: response } = await api.get(
    `/dashboard/match-explanation/${matchId}${params}`
  );
  return response;
};

// === Phase 1.2: Ice Breakers ===
const getIceBreakers = async (matchId: string): Promise<ApiResponse> => {
  const { data: response } = await api.get(
    `/dashboard/ice-breakers/${matchId}`
  );
  return response;
};

const trackIceBreakerUsage = async (
  data: TrackIceBreakerUsageData
): Promise<ApiResponse> => {
  const { data: response } = await api.post(
    '/dashboard/ice-breakers/track-usage',
    data
  );
  return response;
};

// === Phase 2.1: Feedback with Reasons ===
const submitMatchFeedbackWithReasons = async (
  data: SubmitMatchFeedbackWithReasonsData
): Promise<ApiResponse> => {
  const { data: response } = await api.post('/dashboard/decide-match', data);
  return response;
};

// UX-001 FIX: Add getMyMatches function for MatchAlert component
// This wraps getOnboardingMatches to check if user has any matches
interface GetMyMatchesParams {
  page?: number;
  page_size?: number;
}

const getMyMatches = async (
  _params?: GetMyMatchesParams
): Promise<ApiResponse> => {
  // Uses onboarding-matches endpoint which returns all pending matches
  // The page/page_size params are ignored as this endpoint doesn't paginate
  const { data: response } = await api.get('/dashboard/onboarding-matches');
  return response;
};

export {
  getOnboardingMatches,
  updateMatchStatus,
  submitRejectionFeedback,
  getNetworkMatches,
  getMatchCounts,
  getMatchExplanation,
  getIceBreakers,
  trackIceBreakerUsage,
  submitMatchFeedbackWithReasons,
  getMyMatches,
};
